import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { Address } from '@/models/locations';
import { OrderWithContext, OrderDeliveryInfo, OrderDeliveryInfoStatus, OrderDeliveryInfoAddress } from '@/models';

interface ProgressState {
  name: string;
  statuses: OrderDeliveryInfoStatus[];
}

const ORDER_DELIVERY_INFO_STATUS_LOOKUP = {
  [OrderDeliveryInfoStatus.Unknown]: 0,
  [OrderDeliveryInfoStatus.Cancelled]: 0,

  [OrderDeliveryInfoStatus.Scheduled]: 1,
  [OrderDeliveryInfoStatus.Assigned]: 1,
  [OrderDeliveryInfoStatus.EnroutePickup]: 2,
  [OrderDeliveryInfoStatus.PickedUp]: 3,
  [OrderDeliveryInfoStatus.EnrouteDropoff]: 3,
  [OrderDeliveryInfoStatus.Arrived]: 4,
  [OrderDeliveryInfoStatus.Delivered]: 4,
};

@Component({
  name: 'CmgOrderConfirmationDeliveryInfo',
})
export default class CmgOrderConfirmationDeliveryInfo extends Vue {
  progressStates: ProgressState[] = [
    { name: 'Order Received', statuses: [OrderDeliveryInfoStatus.Scheduled, OrderDeliveryInfoStatus.Assigned] },
    { name: 'Headed to Pickup', statuses: [OrderDeliveryInfoStatus.EnroutePickup] },
    { name: `Food's on the Way`, statuses: [OrderDeliveryInfoStatus.PickedUp, OrderDeliveryInfoStatus.EnrouteDropoff] },
    { name: 'Arriving Soon', statuses: [OrderDeliveryInfoStatus.Arrived, OrderDeliveryInfoStatus.Delivered] },
  ];

  @Prop({ required: true, type: Object })
  deliveryInfo!: OrderDeliveryInfo;

  @Prop({ required: true, type: Object })
  order!: OrderWithContext;

  get address(): OrderDeliveryInfoAddress {
    return this.deliveryInfo.dropOffAddress;
  }

  get mapImageUrl(): string {
    return this.$appStore.state.cms.images.icons.map.default;
  }

  get addressImageUrl(): string {
    const customer = this.$appStore.state.customer.profile;
    const deliveryAddress = this.$appStore.state.order.selectedDeliveryAddress;
    if (!deliveryAddress || !deliveryAddress.countryCode || !customer || !customer.country || deliveryAddress.countryCode == customer.country) {
      return this.mapImageUrl;
    }

    return this.$appStore.state.cms.images.countryFlags[deliveryAddress.countryCode.toLowerCase()].default;
  }

  get animationImageUrl(): string {
    const animations = this.$appStore.state.cms.animations.checkout;

    switch (this.status) {
      case OrderDeliveryInfoStatus.Scheduled:
      case OrderDeliveryInfoStatus.Assigned:
        return animations.orderReceived;

      case OrderDeliveryInfoStatus.EnroutePickup:
        return animations.headedToPickup;

      case OrderDeliveryInfoStatus.PickedUp:
      case OrderDeliveryInfoStatus.EnrouteDropoff:
        return animations.onTheWay;

      case OrderDeliveryInfoStatus.Arrived:
      case OrderDeliveryInfoStatus.Delivered:
        return animations.arrivingSoon;

      default:
        return animations.registerPickup;
    }
  }

  get favoriteAddressIconUrl(): string {
    const address: Address = {
      line1: this.deliveryInfo.dropOffAddress.addressLine1,
      line2: this.deliveryInfo.dropOffAddress.addressLine2,
      locality: this.deliveryInfo.dropOffAddress.locality,
      administrativeArea: this.deliveryInfo.dropOffAddress.administrativeArea,
      postalCode: this.deliveryInfo.dropOffAddress.postalCode,
    };

    const isFavorite = this.$appStore.state.customer.deliveryAddresses.find(a => a == address);

    const icons = this.$appStore.state.cms.images.icons;
    return isFavorite ? icons.heartFilled.default : icons.heartEmpty.default;
  }

  get deliveryInstructions(): string | null {
    const delivery = this.order.order.delivery;
    return (delivery && delivery.dropOffInstructions) || null;
  }

  get isLoggedIn(): boolean {
    return this.$services.customer.isLoggedIn() === true;
  }

  get status(): OrderDeliveryInfoStatus {
    return this.deliveryInfo.deliveryStatus;
  }

  get formattedStatus(): string {
    if (!this.deliveryInfo || !this.deliveryInfo.courier) {
      return '';
    }

    switch (this.status) {
      case OrderDeliveryInfoStatus.Scheduled:
      case OrderDeliveryInfoStatus.Assigned:
        return 'Looking for a driver.';

      case OrderDeliveryInfoStatus.EnroutePickup:
        return `${this.deliveryInfo.courier.firstName} is headed to the restaurant.`;

      case OrderDeliveryInfoStatus.PickedUp:
      case OrderDeliveryInfoStatus.EnrouteDropoff:
        return `${this.deliveryInfo.courier.firstName} has picked up your food.`;

      case OrderDeliveryInfoStatus.Arrived:
      case OrderDeliveryInfoStatus.Delivered:
        return `${this.deliveryInfo.courier.firstName} is here with your food.`;

      default:
        return '';
    }
  }

  get deliveryStatus(): string {
    switch (this.status) {
      case OrderDeliveryInfoStatus.Scheduled:
      case OrderDeliveryInfoStatus.Assigned:
        return 'Order Received';

      case OrderDeliveryInfoStatus.EnroutePickup:
        return 'Headed to Pickup';

      case OrderDeliveryInfoStatus.PickedUp:
      case OrderDeliveryInfoStatus.EnrouteDropoff:
        return 'Food is on the way';

      case OrderDeliveryInfoStatus.Arrived:
      case OrderDeliveryInfoStatus.Delivered:
        return 'Arriving soon';

      default:
        return '';
    }
  }

  getProgressStateClass(state: ProgressState): string {
    const currentStateValue = ORDER_DELIVERY_INFO_STATUS_LOOKUP[this.status];
    const stateValue = ORDER_DELIVERY_INFO_STATUS_LOOKUP[state.statuses[0]];

    if (stateValue == currentStateValue) {
      return 'current';
    }

    if (stateValue < currentStateValue) {
      return 'completed';
    }

    if (stateValue > currentStateValue) {
      return 'future';
    }

    return 'unknown';
  }
}
